import React from "react";
import { Modal, Radio, InputNumber } from "antd";
import { Link } from "react-router-dom";
import * as dogsApi from '../api/dogs';
import type { Dog } from '../api/dogs';
import { useAuth } from '../auth/AuthContext';
import { isPremium, isAdmin } from '../utils/userAccess';
import buttonStyles from './Buttons.module.css';
import DogFoodIcon from '../assets/icons/dog-food-pictogram-2-svgrepo-com.svg';

export interface DogProfile {
  name?: string;
  age: string;
  weight: string;
  weight_unit?: 'kg' | 'lb';
  meals_per_day: number;
  body_condition?: string;
  allergies?: string;
}

interface DogProfileModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (profile: DogProfile) => void;
  initialProfile: DogProfile;
  ageOptions: string[];
}

const DogProfileModal: React.FC<DogProfileModalProps> = ({
  open,
  onClose,
  onSubmit,
  initialProfile,
  ageOptions,
}) => {
  const { user } = useAuth();
  const [savedDogs, setSavedDogs] = React.useState<Dog[]>([]);
  const [selectedDogId, setSelectedDogId] = React.useState<number | null>(null);
  
  const [name, setName] = React.useState(initialProfile.name || '');
  const [age, setAge] = React.useState(initialProfile.age);
  // weightValue is the display value in the selected unit
  const [weightUnit, setWeightUnit] = React.useState<'kg' | 'lb'>(initialProfile.weight_unit || 'lb');
  const [weightValue, setWeightValue] = React.useState(() => {
    const w = parseFloat(initialProfile.weight);
    // Initial weight is assumed to be in lbs from backend
    if (initialProfile.weight_unit === 'kg') {
      // If somehow stored as kg, convert to display value
      return isNaN(w) ? 0 : parseFloat((w * 2.20462).toFixed(2));
    }
    // Default: weight is in lbs
    return isNaN(w) ? 0 : w;
  });
  const [mealsPerDay, setMealsPerDay] = React.useState(initialProfile.meals_per_day);
  const [bodyCondition, setBodyCondition] = React.useState(initialProfile.body_condition || '');
  const [allergies, setAllergies] = React.useState(initialProfile.allergies || '');

  React.useEffect(() => {
    if (open && user) {
      loadSavedDogs();
    }
  }, [open, user]);

  const loadSavedDogs = async () => {
    try {
      const dogs = await dogsApi.getDogs();
      setSavedDogs(dogs);
    } catch (err) {
      console.error('Failed to load saved dogs:', err);
    }
  };

  const handleSelectDog = (dogId: number) => {
    const dog = savedDogs.find(d => d.id === dogId);
    if (dog) {
      setSelectedDogId(dogId);
      setName(dog.name);
      setAge(dog.age?.toString() || '');
      setWeightValue(dog.weight || 0);
      setWeightUnit('lb'); // Dogs are saved in lbs
      setMealsPerDay(dog.meals_per_day || 1);
      setBodyCondition(dog.body_condition || '');
      setAllergies(dog.allergies ? dog.allergies.join(', ') : '');
    }
  };

  const handleClearSelection = () => {
    setSelectedDogId(null);
    setName('');
    setAge('');
    setWeightValue(0);
    setWeightUnit('lb');
    setMealsPerDay(1);
    setBodyCondition('');
    setAllergies('');
  };

  React.useEffect(() => {
    setName(initialProfile.name || ''); 
    setAge(initialProfile.age);
    setWeightUnit(initialProfile.weight_unit || 'lb');
    const w = parseFloat(initialProfile.weight);
    // Weight from backend is in lbs by default
    if (initialProfile.weight_unit === 'kg') {
      setWeightValue(isNaN(w) ? 0 : parseFloat((w * 2.20462).toFixed(2)));
    } else {
      setWeightValue(isNaN(w) ? 0 : w);
    }
    setMealsPerDay(initialProfile.meals_per_day);
    setBodyCondition(initialProfile.body_condition || '');
    setAllergies(initialProfile.allergies || '');
  }, [initialProfile]);

  const maxWeight = weightUnit === 'kg' ? 150 : 330;
  const isWeightOverMax = weightValue > maxWeight;
  const handleOk = () => {
    if (isWeightOverMax) return;
    // Always store weight in lbs (convert from kg if needed)
    let weightInLbs = weightValue;
    if (weightUnit === 'kg') {
      weightInLbs = parseFloat((weightValue * 2.20462).toFixed(2));
    }
    onSubmit({ name, age, weight: weightInLbs.toString(), weight_unit: 'lb', meals_per_day: mealsPerDay, body_condition: bodyCondition, allergies });
  };

  return (
    <Modal
      title={<span style={{ fontWeight: 900, fontSize: '1.35rem', color: '#c14444' }}>Tell us about your dog!</span>}
      open={open}
      closable
      onCancel={onClose}
      styles={{ body: { background: '#fffbe6', borderRadius: 18, padding: '2rem 1.5rem' } }}
      footer={[
        <div key="footer-content" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {user && !isPremium(user) && !isAdmin(user) && user.calculations_remaining !== null && user.calculations_remaining !== undefined && (
            <div style={{ 
              textAlign: 'center', 
              padding: '8px 12px', 
              background: user.calculations_remaining === 0 ? '#fff5f0' : '#f5f5f5',
              border: `1px solid ${user.calculations_remaining === 0 ? '#ffe5c2' : '#d4d4d4'}`,
              borderRadius: '8px',
              fontSize: '0.9rem',
              color: '#4a4a4a'
            }}>
              {user.calculations_remaining === 0 ? (
                <>
                  You’ve reached your free calculation limit.
                  <br></br>
                  <Link to="/pricing" style={{ color: '#c14444', textDecoration: 'underline', fontWeight: 600 }}>Upgrade</Link> to continue calculating portions anytime. <img src={DogFoodIcon} alt="" style={{ width: '18px', height: '18px', verticalAlign: 'middle', marginLeft: '4px', filter: 'invert(27%) sepia(51%) saturate(2878%) hue-rotate(346deg) brightness(84%) contrast(97%)' }} />
                  <br></br>
                  You can still browse recipes anytime.
                </>
              ) : (
                <>
                  <strong>{user.calculations_remaining}</strong> of 3 free portion calculation{user.calculations_remaining !== 1 ? 's' : ''} remaining
                </>
              )}
            </div>
          )}
          {user && !isPremium(user) && !isAdmin(user) && user.calculations_remaining === 1 && (
            <div style={{ 
              textAlign: 'center', 
              fontSize: '0.85rem',
              color: '#666',
              marginBottom: '8px'
            }}>
              Tired of re-entering your dog's details? <Link to="/pricing" style={{ color: '#c14444', textDecoration: 'underline' }}>Save a profile with premium</Link>
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
            <button
              key="cancel"
              className={`${buttonStyles.antBtn} ${buttonStyles.antBtnSecondary}`}
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              key="save"
              className={`${buttonStyles.antBtn} ${buttonStyles.antBtnPrimary}`}
              onClick={handleOk}
              disabled={!age || !weightValue || isWeightOverMax}
            >
              Save!
            </button>
          </div>
        </div>
      ]}
    >
      {/* Saved Dogs Selector */}
      {user && (isPremium(user) || isAdmin(user)) && savedDogs.length > 0 && (
        <div style={{ marginBottom: 22, background: '#f7fef9', borderRadius: 12, padding: '0.75rem 1rem', boxShadow: '0 2px 8px rgba(44,62,80,0.07)' }}>
          {!selectedDogId ? (
            <>
              <p style={{ marginBottom: 4, marginTop: 0, fontWeight: 'bold', color: '#15803d', fontSize: '1.08rem', display: 'flex', alignItems: 'center', gap: 8 }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 256 256">
                  <path d="M239.71,125l-16.42-88a16,16,0,0,0-19.61-12.58l-.31.09L150.85,40h-45.7L52.63,24.56l-.31-.09A16,16,0,0,0,32.71,37.05L16.29,125a15.77,15.77,0,0,0,9.12,17.52A16.26,16.26,0,0,0,32.12,144,15.48,15.48,0,0,0,40,141.84V184a40,40,0,0,0,40,40h96a40,40,0,0,0,40-40V141.85a15.5,15.5,0,0,0,7.87,2.16,16.31,16.31,0,0,0,6.72-1.47A15.77,15.77,0,0,0,239.71,125ZM32,128h0L48.43,40,90.5,52.37Zm144,80H136V195.31l13.66-13.65a8,8,0,0,0-11.32-11.32L128,180.69l-10.34-10.35a8,8,0,0,0-11.32,11.32L120,195.31V208H80a24,24,0,0,1-24-24V123.11L107.92,56h40.15L200,123.11V184A24,24,0,0,1,176,208Zm48-80L165.5,52.37,207.57,40,224,128ZM104,140a12,12,0,1,1-12-12A12,12,0,0,1,104,140Zm72,0a12,12,0,1,1-12-12A12,12,0,0,1,176,140Z"></path>
                </svg>
                Use a saved dog profile
              </p>
              <small style={{ color: '#666', fontSize: '0.85rem', display: 'block', marginBottom: 6 }}>
                Auto-fills this form instantly
              </small>
              <select 
                value={selectedDogId || ''} 
                onChange={(e) => {
                  const dogId = e.target.value ? parseInt(e.target.value) : null;
                  if (dogId) {
                    handleSelectDog(dogId);
                  } else {
                    handleClearSelection();
                  }
                }}
                style={{ width: '100%', padding: 10, borderRadius: 8, fontSize: '1rem' }}
              >
                <option value="">Select your dog</option>
                {savedDogs.map(dog => (
                  <option key={dog.id} value={dog.id}>
                    {dog.name} ({dog.age} years, {dog.weight} lbs)
                  </option>
                ))}
              </select>
            </>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontSize: '1rem', color: '#15803d', fontWeight: 600 }}>
                {savedDogs.find(d => d.id === selectedDogId)?.name} ({savedDogs.find(d => d.id === selectedDogId)?.age} yrs, {savedDogs.find(d => d.id === selectedDogId)?.weight} lbs) ✓
              </span>
              <button
                type="button"
                onClick={handleClearSelection}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#15803d',
                  cursor: 'pointer',
                  fontSize: '0.9rem',
                  textDecoration: 'underline',
                  padding: 0
                }}
              >
                Change
              </button>
            </div>
          )}
        </div>
      )}
      {/* Name */}
      <div style={{ marginBottom: 22, background: '#ffe5c2', borderRadius: 12, padding: '1rem 1.2rem', boxShadow: '0 2px 8px rgba(44,62,80,0.07)' }}>
        <p style={{ marginBottom: 8, marginTop: 0, fontWeight: 'bold', color: '#c14444', fontSize: '1.08rem' }}>Dog's Name</p>
        <input
          type="text"
          value={name}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
          placeholder="e.g. Maggie"
          style={{ width: '100%', borderRadius: 8, border: '1px solid #c14444', padding: 8, fontSize: '1rem' }}
        />
      </div>
      {/* Age */}
      <div style={{ marginBottom: 22, background: '#ffe5c2', borderRadius: 12, padding: '1rem 1.2rem', boxShadow: '0 2px 8px rgba(44,62,80,0.07)' }}>
        <p style={{ marginBottom: 8, marginTop: 0, fontWeight: 'bold', color: '#c14444', fontSize: '1.08rem' }}>
          How old is your dog? <span role="img" aria-label="calendar">📅</span>
        </p>
        <Radio.Group onChange={(e) => setAge(e.target.value)} value={age}>
          {ageOptions.map((ageOpt) => (
            <Radio key={ageOpt} value={ageOpt} style={{ marginRight: 10 }}>{ageOpt}</Radio>
          ))}
        </Radio.Group>
      </div>

      {/* Weight */}
      <div style={{ marginBottom: 22, background: '#ffe5c2', borderRadius: 12, padding: '1rem 1.2rem', boxShadow: '0 2px 8px rgba(44,62,80,0.07)' }}>
        <p style={{ marginBottom: 8, marginTop: 0, fontWeight: 'bold', color: '#c14444', fontSize: '1.08rem' }}>
          How much does your dog weigh? <span role="img" aria-label="scale">⚖️</span>
        </p>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <InputNumber
            min={0.5}
                  // Removed max to allow any value
            step={0.1}
            value={weightValue}
            onChange={val => {
              if (typeof val === 'number' && !isNaN(val)) setWeightValue(val);
            }}
                  onBlur={e => {
                    // Show warning if over max, but do not clamp
                    // No-op, warning is shown by isWeightOverMax
                  }}
            onFocus={e => {
              // Select all text on focus to make it easy to replace
              e.target.select();
            }}
            style={{ width: 120, fontSize: '1.1rem', borderRadius: 8, border: '1px solid #c14444' }}
          />
          <span style={{ fontWeight: 600, color: '#c14444', fontSize: '1.08rem' }}>{weightUnit}</span>
          <button
            type="button"
            style={{
              marginLeft: 4,
              padding: '2px 14px',
              borderRadius: 6,
              border: '1px solid #c14444',
              background: weightUnit === 'kg' ? '#ffe5c2' : '#fff',
              color: weightUnit === 'kg' ? '#c14444' : '#888',
              fontWeight: 700,
              cursor: 'pointer',
            }}
            onClick={() => setWeightUnit('kg')}
            aria-pressed={weightUnit === 'kg'}
          >
            kg
          </button>
          <button
            type="button"
            style={{
              marginLeft: 0,
              padding: '2px 14px',
              borderRadius: 6,
              border: '1px solid #c14444',
              background: weightUnit === 'lb' ? '#ffe5c2' : '#fff',
              color: weightUnit === 'lb' ? '#c14444' : '#888',
              fontWeight: 700,
              cursor: 'pointer',
            }}
            onClick={() => setWeightUnit('lb')}
            aria-pressed={weightUnit === 'lb'}
          >
            lb
          </button>
        </div>
        <div style={{ marginTop: 6, color: '#223366', fontSize: '0.98rem', fontWeight: 500 }}>
          {weightUnit === 'kg'
            ? `${(weightValue * 2.20462).toFixed(2)} lb`
            : `${(weightValue / 2.20462).toFixed(2)} kg`}
        </div>
        {isWeightOverMax && (
          <div style={{ color: 'red', fontWeight: 600, marginTop: 4, fontSize: '0.98rem' }}>
            Weight cannot exceed {weightUnit === 'kg' ? '150 kg' : '330 lb'}
          </div>
        )}
      </div>

      {/* Body Condition */}
      <div style={{ marginBottom: 10, background: '#ffe5c2', borderRadius: 12, padding: '1rem 1.2rem', boxShadow: '0 2px 8px rgba(44,62,80,0.07)' }}>
        <p style={{ marginBottom: 8, marginTop: 0, fontWeight: 'bold', color: '#c14444', fontSize: '1.08rem' }}>
          Body Condition <span role="img" aria-label="body">💪</span>
        </p>
        <Radio.Group onChange={(e) => setBodyCondition(e.target.value)} value={bodyCondition}>
          <Radio value="too thin" style={{ marginRight: 10 }}>Too Thin</Radio>
          <Radio value="ideal" style={{ marginRight: 10 }}>Ideal</Radio>
          <Radio value="too heavy" style={{ marginRight: 10 }}>Too Heavy</Radio>
        </Radio.Group>
      </div>

      {/* Meals per day */}
      <div style={{ marginBottom: 10, background: '#ffe5c2', borderRadius: 12, padding: '1rem 1.2rem', boxShadow: '0 2px 8px rgba(44,62,80,0.07)' }}>
        <p style={{ marginBottom: 8, marginTop: 0, fontWeight: 'bold', color: '#c14444', fontSize: '1.08rem' }}>
          How many meals per day? <span role="img" aria-label="bowl">🍲</span>
        </p>
        <Radio.Group onChange={(e) => setMealsPerDay(Number(e.target.value))} value={mealsPerDay}>
          {[1, 2, 3, 4].map((val) => (
            <Radio key={val} value={val} style={{ marginRight: 10 }}>
              {val} {val === 1 ? 'time' : 'times'}
            </Radio>
          ))}
        </Radio.Group>
      </div>

      {/* Allergies */}
      {/* <div style={{ marginBottom: 10, background: '#ffe5c2', borderRadius: 12, padding: '1rem 1.2rem', boxShadow: '0 2px 8px rgba(44,62,80,0.07)' }}>
        <p style={{ marginBottom: 8, marginTop: 0, fontWeight: 'bold', color: '#c14444', fontSize: '1.08rem' }}>
          Allergies? <span role="img" aria-label="allergy">🌾</span>
        </p>
        <textarea
          value={allergies}
          onChange={(e) => setAllergies(e.target.value)}
          placeholder="e.g. chicken, beef, dairy, etc."
          rows={2}
          style={{ width: '100%', resize: 'vertical', borderRadius: 8, border: '1px solid #c14444', padding: 8, fontSize: '1rem' }}
        />
      </div> */}

      {/* Learn More Link */}
      <div style={{ 
        marginTop: 18, 
        padding: '1rem 1.2rem', 
        background: 'linear-gradient(135deg, #fff7f0 0%, #ffe8d6 100%)',
        border: '2px solid #d97706',
        borderRadius: 12,
        textAlign: 'center'
      }}>
        <p style={{ margin: '0 0 0.5rem 0', color: '#7a3a3a', fontSize: '0.95rem' }}>
          Want to learn more about portion sizes?
        </p>
        <Link 
          to="/feeding-portions" 
          style={{ 
            color: '#d97706', 
            fontWeight: 700, 
            fontSize: '1rem',
            textDecoration: 'underline'
          }}
          onClick={onClose}
        >
          View Feeding Portions Guide →
        </Link>
      </div>

      {/* <div style={{ textAlign: 'center', marginTop: 18, color: '#c14444', fontWeight: 600, fontSize: '1.05rem' }}>
        Your answers help us cook up the perfect meal for your furry friend!
      </div> */}
    </Modal>
  );
};

export default DogProfileModal;
